import { Extension } from "@codemirror/state";
import { CompletionContext } from "@codemirror/autocomplete";
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate
} from "@codemirror/view";
import { RangeSetBuilder } from "@codemirror/state";
import { LRLanguage } from "@codemirror/language";

// Autocomplete for %%TAGs%%

// Special tags that should be recognized
const SPECIAL_TAGS = [
  {
    label: "SELECTOR",
    detail: "Selector",
    boost: 99
  },
  {
    label: "ID",
    detail: "Element ID"
  },
  {
    label: "UNIQUE_SLUG",
    detail: "Element Unique Slug",
    info: "Unique slug for element instance (helps identify it on a loop)"
  },
  {
    label: "ELEMENT_NAME",
    detail: "Element Name"
  },
  {
    label: "PARENT_SELECTOR",
    detail: "Parent Selector"
  },
  {
    label: "PARENT_ID",
    detail: "Parent ID"
  },
  {
    label: "POST_ID",
    detail: "Post ID"
  },
  {
    label: "CHILDREN",
    detail: "Children"
  },
  {
    label: "SSR",
    detail: "SSR",
    info: "SSR Content (only available in SSR mode)"
  }
];

const SPECIAL_TAG_REGEX = /%%([a-zA-Z_]+)%%/g;

const specialTagDecoration = Decoration.mark({
  class: "cm-special-tag",
  attributes: { title: "Special Tag" }
});

const specialTagsPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;

    constructor(view: EditorView) {
      this.decorations = this.buildDecorations(view);
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = this.buildDecorations(update.view);
      }
    }

    buildDecorations(view: EditorView): DecorationSet {
      const builder = new RangeSetBuilder<Decoration>();
      const doc = view.state.doc;

      for (const { from, to } of view.visibleRanges) {
        const text = doc.sliceString(from, to);
        let match;
        let lastIndex = 0;

        // Reset regex state
        SPECIAL_TAG_REGEX.lastIndex = 0;

        while ((match = SPECIAL_TAG_REGEX.exec(text)) !== null) {
          const tagName = match[1];
          const fullMatch = match[0]; // This is the complete %%TAG%%

          // Check if it's a known special tag
          if (SPECIAL_TAGS.some(t => t.label === tagName)) {
            const start = from + match.index;
            const end = start + fullMatch.length;

            // Ensure we don't have overlapping decorations
            if (start >= lastIndex) {
              builder.add(start, end, specialTagDecoration);
              lastIndex = end;
            }
          }
        }
      }

      return builder.finish();
    }
  },
  {
    decorations: v => v.decorations
  }
);

// Extension to add the parser
export const specialTagsParser: Extension = [
  specialTagsPlugin,
  EditorView.theme({
    ".cm-special-tag, .cm-special-tag *": {
      color: "#ff79c6"
    }
  })
];

// Function to get all special tags for autocomplete
export function getTags(tags: string[]): Array<{
  label: string;
  displayLabel: string;
}> {
  const tagsLowerCase = tags.map(tag => tag.toLowerCase().replaceAll("_", ""));
  return SPECIAL_TAGS.filter(tag => {
    return tagsLowerCase.includes(tag.label.toLowerCase().replaceAll("_", ""));
  }).map(tag => ({
    label: `%%${tag.label}%%`,
    displayLabel: tag.detail,
    info: tag.info,
    boost: tag.boost ?? 0
  }));
}

// Autocomplete function for special tags
export function completeTags(context: CompletionContext, filterTags: string[]) {
  const wordBefore = context.matchBefore(/%%[a-zA-Z_]*/);
  if (!wordBefore) return null;

  const tags = getTags(filterTags);

  return {
    from: wordBefore.from,
    options: tags,
    validFor: /^%%[a-zA-Z_]*$/
  };
}

export function createTagsCompletions(
  tags: string[],
  languageDefinition: LRLanguage
) {
  return [
    specialTagsParser,
    languageDefinition.data.of({
      autocomplete: (context: CompletionContext) => completeTags(context, tags)
    })
  ];
}
